import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardHeader, Button, Collapse, CardBody } from 'reactstrap';
import StatusCode from '../../../../../../util/StatusCode';
import Loading from '../../../../../misc/Loading';
import SelectInput from '../../../../../misc/SelectInput';
import { GetConsumers } from '../../../../../../store/ConsumerAction';
import { MasterFlowSyncConsumer } from '../../../../../../models/MasterFlowSyncConsumer';
import { RequestState } from '../../../../../../models/RequestState';
import { RootState } from '../../../../../../store/store';
import { CoreSS12000ApiConfiguration } from '../../../../../../models/CoreSS12000ApiConfiguration';
import Copy from "../../../../../misc/Copy";


interface SS12000ApiConfigurationProps {
    disabled: boolean
    configuration: CoreSS12000ApiConfiguration
    updatePart: (part: Partial<CoreSS12000ApiConfiguration>) => void
}

const SS12000ApiConfiguration = (props: SS12000ApiConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);
    const [selectedConsumer, setSelectedConsumer] = useState<string | undefined>(undefined);
    const dispatch = useDispatch();
    const consumers = useSelector<RootState, RequestState<MasterFlowSyncConsumer[]>>(state => state.consumers.consumers);

    useEffect(() => {
        GetConsumers()(dispatch);
    }, [dispatch]);

    const addConsumer = (selectedConsumer: string | undefined) => {
        if (!selectedConsumer) return;

        let updateConsumers = [...props.configuration.consumers];
        updateConsumers.push(selectedConsumer);

        props.updatePart({ consumers: updateConsumers })
        setSelectedConsumer(undefined);
    }

    const removeConsumer = (selectedConsumer: string) => {
        let configurationConsumers = [...props.configuration.consumers];

        let index = configurationConsumers.indexOf(selectedConsumer);
        if (index > -1) {
            configurationConsumers.splice(index, 1);
        }

        props.updatePart({ consumers: configurationConsumers })
        setSelectedConsumer(undefined);
    }

    const renderConsumers = () => {
        switch (consumers.code) {
            case StatusCode.PENDING: {
                return (<Loading />);
            }
            case StatusCode.ERROR: {
                return (<p>{t('ss12000Api.error', { ns: 'configuration' })}</p>);
            }
            case StatusCode.COMPLETE: {
                if (!consumers.data.length) {
                    return (<p>{t('ss12000Api.noResult', { ns: 'configuration' })}</p>);
                } else {
                    return (
                        <div className="picker-list-component">
                            {props.configuration.consumers.map(c => {
                                const found = consumers.data.find(oc => oc.id === c);
                                const displayName = found?.displayName ?? `${t('ss12000Api.unknown', { ns: 'configuration' })} (${c})`
                                return (
                                    <span key={c} className="picker-list-item list">
                                        <span className="displayName">{displayName}</span>
                                        <button
                                            onClick={() => removeConsumer(c)}
                                            title={t('action.remove', { ns: 'common' })}
                                            className="icon-btn"
                                            disabled={props.disabled}>
                                            <FontAwesomeIcon icon="trash" /></button>
                                    </span>
                                )
                            })}

                            <span className="picker-list-item select">
                                <SelectInput
                                    items={consumers.data
                                        .filter(c => !props.configuration.consumers.some(x => x === c.id))
                                        .map(c => ({ value: c.id, text: c.displayName }))}
                                    onChange={(name, value) => setSelectedConsumer(value)}
                                    name='consumers'
                                    selected={selectedConsumer}
                                    defaultText={t('ss12000Api.addConsumerDefaultSelect', { ns: 'configuration' })}
                                    disabled={props.disabled}
                                    className='picker-list-select'
                                />
                                <Button color="primary"
                                    disabled={!selectedConsumer || props.disabled}
                                    className="btn-primary"
                                    onClick={() => addConsumer(selectedConsumer)}>
                                    {t('action.add', { ns: 'common' })}</Button>
                            </span>
                        </div>
                    );
                }
            }
            default: return;
        }
    }

    const webUrl = `/api/SS12000/v2020/${props.configuration.moduleId }/activities`

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.SS12000API.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <h5>{t('ss12000Api.api-information', {ns: 'configuration'})}</h5>
                        <p><span className="bold">{t('ss12000Api.url', { ns: 'configuration' })} </span>
                            {webUrl}<Copy value={webUrl} /></p>

                        <h5>{t('ss12000Api.header', { ns: 'configuration' })}</h5>
                        {renderConsumers()}

                    </div>
                </CardBody>
            </Collapse>
        </Card>
    );
}

export default SS12000ApiConfiguration;
